import React, { forwardRef, useCallback, useContext, useMemo, useState } from 'react';
import { ButtonBase, Box, makeStyles, ClickAwayListener } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { ReactComponent as SwitchOn } from '../../../../static/images/SwitchOn.svg';
import { ReactComponent as SwitchOff } from '../../../../static/images/SwitchOff.svg';
import { classNames, downloadFileById, getArrayRandomKey, useGetUserInfo } from '../../../../utils';
import {
  CloseIcon,
  DocIcon,
  DropDownIcon,
  FilterIcon,
  FullScreenIcon,
  FullScreenOffIcon,
  NewSettingIcon,
} from '../../../../components/Icon';
import { closeFullScreen, openFullScreen } from '../../../../redux/actions';
import { AdmissionContext } from '../..';
import { eadmissionAPI } from '../../../../apis';
import { AutoLogoutBtn, MuteBtn } from '../../Actionable/ActionableHeader';
import exportPwdExcel from '../../../../utils/exportPwdExcel';
import { LOGIN_INFO } from '../../../../comm/constant';
import { WarpSwitch } from '../../../../components/WarpSwitch';
import RefreshButton from '../../../../components/RefreshButton';
import useCheckLogin from '../../../../utils/useCheckLogin';
import { i18n } from '../../../../language';

const useStyles = makeStyles((theme) => ({
  border: {
    height: '2rem',
    width: '1px',
    backgroundColor: '#E4E7EB',
  },
  tab: {
    fontSize: '1.125rem',
    color: '#538FFF',
    fontWeight: 'bold',
  },
  title: {
    fontSize: '1.125rem',
    color: '#3f5375',
    fontWeight: 'bold',
  },
  unSelected: {
    opacity: '0.4',
  },
  settingBtn: ({ fullScreen }) => ({
    color: '#538fff',
    border: fullScreen ? 'unset' : 'solid 1px #bad2ff',
    borderRadius: '8px',
    height: '2.5rem',
    padding: '0.8rem',
  }),
  settingIcon: {
    fontSize: '1.5rem',
  },
  fullScreenBtn: {
    width: '2.938rem',
    height: '2.5rem',
    backgroundColor: '#538fff',
    color: '#fff',
  },
  showBusStop: {
    fontSize: '0.75rem',
    color: '#7D86A9',
    gap: '0.3rem',
    fontFamily: 'NotoSans-Medium',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  forMyAction: {
    backgroundColor: '#FDFEFF',
    fontSize: '0.75rem',
    color: '#7D86A9',
    gap: '0.3rem',
    fontFamily: 'NotoSans-Medium',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  showSOPDDetails: {
    fontSize: '1rem',
    color: '#3E3E3E',
    gap: '0.3rem',
    fontFamily: 'NotoSans-Medium',
    display: 'flex',
    alignItems: 'center',
  },
  allAction: {
    width: '10.5rem',
    height: '2.938rem',
    border: 'unset',
    borderRadius: '8px',
    backgroundColor: '#538FFF',
    fontSize: '1rem',
    color: '#FFFFFF',
    gap: '0.75rem',
    fontFamily: 'NotoSans-Medium',
  },
  HandIcon: {
    fontSize: '2rem',
  },
  filter: {
    width: '6.75rem',
    height: '2.5rem',
    border: '1px solid #E4E7EB',
    borderRadius: '8px',
    backgroundColor: '#FDFEFF',
    fontSize: '1rem',
    color: '#7D86A9',
    gap: '0.43rem',
    fontFamily: 'NotoSans-Medium',
  },
  filterIcon: {
    fontSize: '1.88rem',
    color: '#538fff',
  },
  filterIconAll: {
    fontSize: '1.88rem',
    color: '#fff',
    marginRight: '0.625rem',
  },
  filterBox: {
    display: 'flex',
    background: '#538fff',
    marginRight: '0.938rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '0.5rem',
  },
  filterAll: {
    width: '6.25rem',
    height: '2.5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#fff',
  },
  closeIcon: {
    width: '1rem',
    height: '1rem',
    color: '#fff',
  },
  export: {
    width: '6.75rem',
    height: '2.5rem',
    borderRadius: '8px',
    backgroundColor: '#538FFF',
    fontSize: '1rem',
    color: '#FFF',
    gap: '0.5rem',
    fontFamily: 'NotoSans-Medium',
  },
  docIcon: {
    fontSize: '1.25rem',
    color: '#FFF',
  },
  switch: {
    width: '2.75rem',
    height: '1.47rem',
  },
  switchViewBtn: {
    height: '100%',
    color: '#2E4469',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gridGap: '0.571rem',
  },
  viewTextLabel: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    fontFamily: 'NotoSans-Medium',
    textWrap: 'nowrap',
  },
  dropdownIcon: {
    fontSize: '0.875rem',
    color: theme.palette.primary.main,
  },
  dropDownList: {
    zIndex: 999,
    position: 'absolute',
    top: '3rem',
    left: 0,
    background: '#FFFFFF',
    display: 'flex',
    padding: '0.285rem 0',
    flexDirection: 'column',
    borderRadius: '0.625rem',
    boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.1)',
  },
  dropDownItem: {
    color: '#2E4469',
    fontSize: '1rem',
    fontWeight: 300,
    cursor: 'pointer',
    padding: '0.571rem 1rem',
    minWidth: 'max-content',

    '&:hover': {
      color: '#538fff',
      background: '#f5faff',
    },
  },
  flexSpace: {
    display: 'block',
    minWidth: 0,
    flex: 1,

    [`@media (max-width: 1420px)`]: {
      display: 'none',
    },
  },
}));

const FullScreenButton = ({ fullScreen, handleFullScreen }) => {
  const classes = useStyles({ fullScreen });
  return fullScreen ? (
    <ButtonBase className={classes.fullScreenBtn} onClick={handleFullScreen}>
      <FullScreenOffIcon className={classes.settingIcon} />
    </ButtonBase>
  ) : (
    <ButtonBase className={classes.settingBtn} onClick={handleFullScreen}>
      <FullScreenIcon className={classes.settingIcon} />
    </ButtonBase>
  );
};

const Header = forwardRef(({ isLocationView, ...props }, ref) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const loginInfo = useSelector((state) => state.loginInfo);
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const {
    tabValue,
    setTabValue,
    tabValues,
    showBusStop,
    setShowBusStop,
    forMyAction,
    setForMyAction,
    showSOPDDetails,
    setShowSOPDDetails,
    setFilterOpen,
    filter,
    setFilter,
    admissionParamsRef,
    initFilter,
    eadViewVisible,
  } = useContext(AdmissionContext);
  const userInfo = useGetUserInfo();
  const checkLogin = useCheckLogin();
  const [showSwitchDropdown, setShowSwitchDropdown] = useState(false);

  const handleClose = () => {
    setFilter(initFilter());
  };

  const handleFullScreen = () => {
    if (fullScreen) {
      dispatch(closeFullScreen());
    } else {
      dispatch(openFullScreen());
    }
  };

  const handleShowBusStop = () => {
    setShowBusStop((preData) => ({
      type: 'all',
      show: !preData.show,
    }));
  };

  const handleExport = useCallback(
    async (protectKey) => {
      try {
        if (admissionParamsRef?.current?.admissionParams) {
          let isMask = true;
          const loginInfoStorage = JSON.parse(localStorage.getItem(LOGIN_INFO));

          const tokenExp = loginInfoStorage?.tokenExp;
          if (moment(tokenExp).isAfter(moment())) {
            isMask = false;
          }

          await downloadFileById(
            () => {
              return eadmissionAPI.getAdmissionExportRequest({
                ...admissionParamsRef?.current?.admissionParams,
                hospId: userInfo?.hospId,
                wardId: userInfo?.wardId,
                protectKey,
                isMask,
              });
            },
            () => {
              return `Admission Export Excel_${moment().valueOf()}`;
            }
          );
        }
      } catch (error) {}
    },
    [tabValue, userInfo?.hospId, admissionParamsRef]
  );

  // 判断是否已经filter
  const filterSelected = useMemo(() => {
    if (isLocationView) {
      return false;
    }

    return (
      initFilter().aeNo === filter.aeNo &&
      initFilter().sex === filter.sex &&
      filter.priority?.length === 0 &&
      filter.handingByIds?.length === 0 &&
      filter.status?.length === 0 &&
      filter[`busstop_${tabValue}`]?.length === 0 &&
      initFilter().chiName === filter.chiName &&
      initFilter().diagnosis === filter.diagnosis &&
      initFilter().engName === filter.engName &&
      initFilter().hospCode === filter.hospCode &&
      initFilter().remark === filter.remark &&
      filter.specialtys?.length === 0 &&
      moment(initFilter().startDate).isSame(filter.startDate, 'days') &&
      moment(initFilter().endDate).isSame(filter.endDate, 'days') &&
      initFilter().waitTime === filter.waitTime
    );
  }, [JSON.stringify(filter), tabValue]);

  const viewDropDownOptions = [
    {
      text: 'E-Admission',
      value: tabValues.Pending,
      isShow: eadViewVisible?.transferIn?.eadmVisible,
    },
    {
      text: 'AED View',
      value: tabValues.AEDView,
      isShow: eadViewVisible?.transferIn?.eadmVisible && eadViewVisible?.aedView,
    },
    {
      text: i18n.t('EADMISSION_TRANSFER_IN'),
      value: tabValues.TransferInPending,
      isShow: eadViewVisible?.transferIn?.requestInVisible,
    },
    {
      text: i18n.t('EADMISSION_SOPD_LOC_VIEW'),
      value: tabValues.SOPDView,
      isShow: eadViewVisible?.sopdView,
    },
  ];
  const visibleOptions = _.filter(viewDropDownOptions, (item) => item?.isShow);
  const isTransferInView = [tabValues.TransferInPending, tabValues.TransferInCompleted].includes(tabValue);
  const isEAdmissionView = [tabValues.Pending, tabValues.Completed].includes(tabValue);
  const pendingTabValue = isTransferInView ? tabValues.TransferInPending : tabValues.Pending;
  const completedTabValue = isTransferInView ? tabValues.TransferInCompleted : tabValues.Completed;
  const showMuteBtn = [
    tabValues.Pending,
    tabValues.Completed,
    tabValues.TransferInPending,
    tabValues.TransferInCompleted,
  ].includes(tabValue);

  const getSwitchViewBtnText = () => {
    if (isTransferInView) {
      return i18n.t('EADMISSION_TRANSFER_IN');
    } else if (isEAdmissionView) {
      return 'E-Admission';
    }
    return _.find(viewDropDownOptions, (item) => item?.value === tabValue)?.text;
  };

  const handleSwitchOnClick = (value) => {
    setShowSwitchDropdown(false);
    setTabValue(value);
  };

  const SwitchViewButton = () => {
    return (
      <ClickAwayListener onClickAway={() => setShowSwitchDropdown(false)}>
        <Box position="relative">
          <Box
            className={classes.switchViewBtn}
            display="flex"
            alignItems="center"
            gridGap="0.285rem"
            onClick={() => setShowSwitchDropdown(!showSwitchDropdown)}
          >
            <Box className={classes.viewTextLabel}>{getSwitchViewBtnText()}</Box>
            <DropDownIcon className={classes.dropdownIcon} />
          </Box>

          {showSwitchDropdown && (
            <Box className={classes.dropDownList}>
              {_.map(visibleOptions, (item, index) => (
                <Box
                  key={getArrayRandomKey(index, 'bed-status-switch-view-type-btn')}
                  onClick={() => handleSwitchOnClick(item?.value)}
                  className={classes.dropDownItem}
                >
                  {item?.text}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </ClickAwayListener>
    );
  };

  return (
    <Box
      ref={ref}
      width="100%"
      display="flex"
      alignItems="center"
      bgcolor="#FFF"
      borderBottom="solid 1px #E4E7EB"
      p="0.2rem 2.5rem"
      flexWrap="wrap"
      gridGap="0.571rem"
    >
      <RefreshButton handleRefresh={() => admissionParamsRef?.current?.setRefresh?.((o) => !o)} />
      <SwitchViewButton />
      {(isTransferInView || isEAdmissionView) && (
        <>
          <div className={classes.border} />
          <ButtonBase
            className={tabValue === pendingTabValue ? classes.tab : classNames(classes.tab, classes.unSelected)}
            onClick={() => setTabValue(pendingTabValue)}
          >
            Pending
          </ButtonBase>
          <div className={classes.border} />
          <ButtonBase
            className={tabValue === completedTabValue ? classes.tab : classNames(classes.tab, classes.unSelected)}
            onClick={() => setTabValue(completedTabValue)}
          >
            Completed
          </ButtonBase>
        </>
      )}

      <div className={classes.border} />
      <ButtonBase
        className={classNames(classes.tab, classes.unSelected)}
        onClick={() => history.push('/actionable?type=AD')}
      >
        Actionable
      </ButtonBase>
      <div className={classes.border} />
      <ButtonBase
        className={classNames(classes.tab, classes.unSelected)}
        onClick={() => history.push('/actionableDashboardAdm?type=AD&ticketType=history')}
      >
        Ack. History
      </ButtonBase>
      {tabValue === tabValues.SOPDView && (
        <>
          <div className={classes.border} />
          <Box className={classes.showSOPDDetails}>
            Show Details
            <WarpSwitch checked={showSOPDDetails} onChange={(event) => setShowSOPDDetails(event.target.checked)} />
          </Box>
        </>
      )}
      <Box className={classes.flexSpace} />
      {showMuteBtn && <MuteBtn height="2.5rem" />}
      {(tabValue === tabValues.Pending || tabValue === tabValues.Completed) && (
        <>
          <div className={classes.border} />
          <AutoLogoutBtn module="admission" />
          {tabValue === tabValues.Pending && (
            <>
              <div className={classes.border} />
              <ButtonBase className={classes.showBusStop} onClick={handleShowBusStop}>
                Show Bus Stop
                {showBusStop.show ? <SwitchOn className={classes.switch} /> : <SwitchOff className={classes.switch} />}
              </ButtonBase>
            </>
          )}
          <div className={classes.border} />
          <ButtonBase className={classes.forMyAction} onClick={() => setForMyAction((o) => !o)}>
            My Action Only
            {forMyAction ? <SwitchOn className={classes.switch} /> : <SwitchOff className={classes.switch} />}
          </ButtonBase>
          <div className={classes.border} />
          {filterSelected ? (
            <ButtonBase className={classes.filter} onClick={() => setFilterOpen(true)}>
              <FilterIcon className={classes.filterIcon} />
              Filter
            </ButtonBase>
          ) : (
            <div className={classes.filterBox}>
              <ButtonBase id="bed_filter_btn1" className={classes.filterAll} onClick={() => setFilterOpen(true)}>
                <FilterIcon className={classes.filterIconAll} />
                Filter
              </ButtonBase>
              <ButtonBase id="bed_filter_btn2" style={{ padding: '0 1rem' }}>
                <CloseIcon className={classes.closeIcon} onClick={handleClose} />
              </ButtonBase>
            </div>
          )}
          <div className={classes.border} />
          <ButtonBase
            className={classes.export}
            onClick={() => {
              if (!checkLogin()) return;
              exportPwdExcel(handleExport)();
            }}
          >
            <DocIcon className={classes.docIcon} />
            Export
          </ButtonBase>
        </>
      )}
      {loginInfo.flag && (
        <>
          <div className={classes.border} />
          <ButtonBase className={classes.settingBtn} onClick={() => history.push('/admissionSetting')}>
            <NewSettingIcon className={classes.settingIcon} />
          </ButtonBase>
        </>
      )}
      <div className={classes.border} />
      <FullScreenButton fullScreen={fullScreen} handleFullScreen={handleFullScreen} />
    </Box>
  );
});

export default Header;
